import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Main as Container } from 'Components/Main';
import { PageHelper } from 'Components/PageHelper';
import { FormSuccess } from 'Components/Form/Success';
import { Detalhes } from './Details';
import { addData, editData, getDataById } from 'services/datas-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { Dialog, PageHeader } from 'Components/ui';

const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';

const CancelBtn = styled.button`
  height: 44px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SaveBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${BLUE_DEEP}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const DataForm = () => {
  const { t } = useTranslation(['mapping']);
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const navigate = useNavigate();
  const [idData, setIdData] = useState(null);
  const [finished, setFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, control, setValue, reset } = useForm({
    defaultValues: { name: '', tags: [], description: '', companyKey: selectedTenant.slug },
  });

  const handleComplete = async (values) => {
    try {
      setIsSubmitting(true);
      const res = id ? await editData(id, values) : await addData(values);
      if (res.status === 200 || res.status === 201) {
        setIdData(res.data._id ?? id);
        setFinished(true);
      } else {
        throw new Error(t('mapping:dataTypes.notifications.saveError'));
      }
    } catch (error) {
      Dialog.alert({
        title: t('mapping:dataTypes.notifications.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdData(null);
    navigate('/data/add');
  };

  const getData = useCallback(async () => {
    try {
      const resData = await getDataById(id);
      Object.keys(resData.data).forEach(key => setValue(key, resData.data[key]));
    } catch (error) {
      Dialog.alert({
        title: t('mapping:dataTypes.notifications.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  }, [id, setValue, t]);

  useEffect(() => {
    if (!id) return;
    getData();
  }, [id, getData]);

  const tourSteps = [
    { target: 'body', content: t('mapping:dataTypes.tour.form.welcome'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="page-title"]', content: t('mapping:dataTypes.tour.form.title'), disableScrolling: true },
    { target: '[data-tour="data-name"]', content: t('mapping:dataTypes.tour.form.name'), disableScrolling: false },
    { target: '[data-tour="data-tags"]', content: t('mapping:dataTypes.tour.form.tags'), disableScrolling: false },
    { target: '[data-tour="data-description"]', content: t('mapping:dataTypes.tour.form.description'), disableScrolling: false },
    { target: 'body', content: t('mapping:dataTypes.tour.form.complete'), disableScrolling: true, placement: 'center' },
  ];

  return (
    <Container>
      {!finished && (
        <form onSubmit={handleSubmit(handleComplete)}>
          <PageHeader
            data-tour="page-title"
            title={id ? t('mapping:dataTypes.form.editTitle') : t('mapping:dataTypes.form.addTitle')}
            segments={[
              { label: t('common:breadcrumbs.mapping') },
              { label: t('mapping:dataTypes.list.title'), route: '/data' },
              { label: id ? t('mapping:dataTypes.form.editTitle') : t('mapping:dataTypes.form.addTitle') },
            ]}
            actions={
              <>
                <CancelBtn type="button" onClick={() => navigate('/data')}>
                  {t('mapping:dataTypes.form.cancel')}
                </CancelBtn>
                <SaveBtn type="submit" disabled={isSubmitting}>
                  {t('mapping:dataTypes.form.save')}
                </SaveBtn>
              </>
            }
          />
          <Detalhes control={control} register={register} errors={errors} id={id} />
        </form>
      )}
      {finished && (
        <FormSuccess
          title={t('mapping:dataTypes.form.success')}
          labelStartAgain={t('mapping:dataTypes.form.addAnother')}
          handleStartAgain={handleReset}
          labelShow={t('mapping:dataTypes.form.viewDetails')}
          handleShow={() => navigate('/data/show/' + idData)}
        />
      )}
      {!finished && <PageHelper steps={tourSteps} tourKey="data-form" />}
    </Container>
  );
};
